import React, { useEffect, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import adminService from '../services/adminService';
import './AdminPortal.css';

const ROLE_OPTIONS = [
  { value: 'department_chief', label: 'Department Chief' },
  { value: 'department_responder', label: 'Department Responder' },
  { value: 'site_admin', label: 'Site Admin' },
  { value: 'public', label: 'Public (default)' },
];

function formatRole(roleName) {
  const option = ROLE_OPTIONS.find((opt) => opt.value === roleName);
  if (option) return option.label;
  // Fallback: snake_case -> Title Case.
  return roleName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function AdminPortal() {
  const { addToast } = useToast();

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentRoles, setCurrentRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('department_chief');
  const [departmentId, setDepartmentId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminService.listRoles()
      .then((data) => setRoles(data.roles || []))
      .catch((err) => addToast(err.message || 'Failed to load roles', 'error'));

    adminService.listDepartments()
      .then((data) => setDepartments(data.departments || []))
      .catch((err) => addToast(err.message || 'Failed to load departments', 'error'));
  }, [addToast]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;
    setLoading(true);
    try {
      const data = await adminService.searchUsers(searchEmail.trim());
      setUsers(data.users || []);
      setSelectedUser(null);
      setCurrentRoles([]);
    } catch (err) {
      addToast(err.message || 'Search failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    try {
      const data = await adminService.getUserRoles(user.id);
      setCurrentRoles(data.roles || []);
    } catch (err) {
      addToast(err.message || 'Failed to load user roles', 'error');
      setCurrentRoles([]);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setLoading(true);
    try {
      const deptId = departmentId.trim() || null;
      const data = await adminService.assignRole(selectedUser.id, selectedRole, deptId);
      setCurrentRoles(data.roles || []);
      addToast(`Granted ${formatRole(selectedRole)} to ${selectedUser.email}`, 'success');
    } catch (err) {
      addToast(err.message || 'Failed to assign role', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      addToast('Copied department ID to clipboard', 'success');
    } catch {
      addToast('Failed to copy ID', 'error');
    }
  };

  return (
    <div className="admin-portal">
      <h1>Admin Portal</h1>

      <section className="admin-section">
        <h2>Grant Role to User</h2>
        <form onSubmit={handleSearch} className="admin-form">
          <label>
            Search by email
            <input
              type="email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </label>
          <button type="submit" className="admin-button" disabled={loading}>
            Search
          </button>
        </form>

        {users.length > 0 && (
          <ul className="admin-results">
            {users.map((u) => (
              <li
                key={u.id}
                className={selectedUser?.id === u.id ? 'selected' : ''}
                onClick={() => handleSelectUser(u)}
              >
                <strong>{u.name || '(no name)'}</strong> — {u.email}
              </li>
            ))}
          </ul>
        )}

        {selectedUser && (
          <form onSubmit={handleAssign} className="admin-form" style={{ marginTop: '1rem' }}>
            <label>
              Role
              <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            {selectedRole === 'department_responder' && (
              <label>
                Department
                <select
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                >
                  <option value="">Select a department...</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.type}) — {d.id}
                    </option>
                  ))}
                  {departments.length === 0 && (
                    <option value="" disabled>No departments yet</option>
                  )}
                </select>
              </label>
            )}
            <button type="submit" className="admin-button" disabled={loading}>
              Grant Role
            </button>
          </form>
        )}

        {selectedUser && currentRoles.length > 0 && (
          <div className="current-roles">
            <strong>Current roles for {selectedUser.email}:</strong>
            <ul>
              {currentRoles.map((r, i) => (
                <li key={i}>
                  {formatRole(r.role)}
                  {r.department_name && ` — ${r.department_name}`}
                  {r.department_id && !r.department_name && ` — dept ${r.department_id}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="admin-section">
        <h2>Departments</h2>
        {departments.length === 0 ? (
          <p>No departments have been created yet.</p>
        ) : (
          <ul className="admin-results">
            {departments.map((d) => (
              <li key={d.id}>
                <strong>{d.name}</strong> ({d.type})
                <br />
                <small>
                  Chief: {d.chief_name || '(unknown)'} ({d.chief_email || 'no email'})
                  <br />
                  Join code: <code>{d.join_code}</code>
                  <br />
                  ID: <code>{d.id}</code>{' '}
                  <button
                    type="button"
                    className="admin-button"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                    onClick={() => copyId(d.id)}
                  >
                    Copy ID
                  </button>
                </small>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="admin-section">
        <h2>System Roles</h2>
        <ul className="role-list">
          {roles.map((r) => (
            <li key={r.id}>
              <strong>{formatRole(r.name)}</strong>
              <code> ({r.name})</code>
              {r.is_internal && <em> (internal)</em>}
              <br />
              <small>{r.description}</small>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default AdminPortal;
